import DataGrid from "../../components/DataGrid";
import { useCallback, useEffect, useMemo } from "react";
import { contractsConfig as config } from "./contractsConfig";
import TableTitleRow from "../../components/TableTitleRow";
import { Contract, SalesContractsDropdowns } from "../../types";
import useVisibleColumns from "../../hooks/useVisibleColumns";
import { ViewProvider } from "../../contexts/ViewContext";
import { api } from "../../api";
import { contractFromExternalList } from "../../hooks/useSetDataByView/convert";
import { CONTRACTS_DEFAULTS } from "../../helpers/defaultUrl";
import { useDataGridQuery, useUrlState } from "../../hooks/useUrlState";
import { DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { contractsSortFieldMap } from "../../helpers/sortFieldMap";
import { contractsSearchFieldMap } from "../../helpers/searchFieldMap";
import { extractApiError } from "../../api/errors";
import { getVisibleSearchFields } from "../../helpers/getVisibleSearchFields";
import { useShowToast } from "../../contexts/ToastContext.tsx";
import { TableDialogs } from "../../components/TableDialogs.tsx";
import { ConfirmDialog } from "primereact/confirmdialog";
import {
  cityCountryPairLabel,
  cityCountryPairValue,
  parseCityCountryPairValue,
} from "../../helpers/cityCountryPair.ts";
import {
  booleanDropdownOptions,
  resolveBooleanOption,
} from "../../helpers/booleanDropdownOptions.tsx";

const columns = config.columns;

export default function ContractsView() {
  const [visibleColumns, setVisibleColumns] = useVisibleColumns(columns);
  const showToast = useShowToast();

  const { page, limit, sort, order, onPageChange, onSortChange } =
    useDataGridQuery({
      page: CONTRACTS_DEFAULTS.page,
      limit: CONTRACTS_DEFAULTS.limit,
      sort: CONTRACTS_DEFAULTS.sort,
      order: CONTRACTS_DEFAULTS.order,
    });

  const {
    filters,
    dateFrom,
    dateTo,
    showCompletedRows,
    globalFilterValue,
    setGlobalFilterValue,
  } = useUrlState();

  const debouncedSearchValue = useDebouncedValue(globalFilterValue, 300);

  // search
  const visibleSearchFields = useMemo(
    () => getVisibleSearchFields(visibleColumns, contractsSearchFieldMap),
    [visibleColumns],
  );

  //sorting
  const orderByField = useMemo(() => {
    const mapped = contractsSortFieldMap[sort];
    const base = mapped ?? CONTRACTS_DEFAULTS.sort;
    const direction = order === -1 ? "-" : "";
    return `${direction}${base}`;
  }, [sort, order]);

  const filteredCityAndCountry = useMemo(() => {
    if (!filters.city) {
      return undefined;
    }
    return parseCityCountryPairValue(filters.city);
  }, [filters.city]);

  const {
    data: rawData,
    isLoading,
    error,
  } = api.endpoints.listContractsApiContractsGet.useQuery({
    orderBy: `${orderByField},-created_at`,
    contractType: "sales",
    limit: limit,
    offset: page * limit,
    search: debouncedSearchValue || undefined,
    searchFields: visibleSearchFields,
    // filters
    clientId: filters.client,
    productId: filters.product,
    entryStatus: !showCompletedRows ? "default,pending" : undefined,
    dateFrom: dateFrom ? dateFrom.toISOString().split("T")[0] : undefined,
    dateTo: dateTo ? dateTo.toISOString().split("T")[0] : undefined,
    city: filteredCityAndCountry?.city,
    country: filteredCityAndCountry?.country,
    signed: resolveBooleanOption(filters.signed),
  });

  const data: Contract[] = useMemo(() => {
    if (!rawData) {
      return [];
    }
    return rawData.items.map(contractFromExternalList);
  }, [rawData]);

  useEffect(() => {
    if (error) {
      showToast({
        severity: "error",
        summary: "Błąd ładowania danych",
        detail: extractApiError(error),
      });
    }
  }, [error, showToast]);

  const { data: products } = api.endpoints.listProductsApiProductsGet.useQuery({
    limit: 10000,
    orderBy: "name",
  });
  const { data: suppliers } =
    api.endpoints.listCompaniesApiCompaniesGet.useQuery({
      limit: 10000,
      isSupplier: true,
      orderBy: "name",
    });
  const { data: buyers } = api.endpoints.listCompaniesApiCompaniesGet.useQuery({
    limit: 10000,
    isBuyer: true,
    orderBy: "name",
  });
  const { data: transporters } =
    api.endpoints.listCompaniesApiCompaniesGet.useQuery({
      limit: 10000,
      isTransporter: true,
      orderBy: "name",
    });
  const { data: drivers } = api.endpoints.listDriversApiDriversGet.useQuery({
    limit: 10000,
    orderBy: "last_name",
  });
  const { data: locations } =
    api.endpoints.listLocationsApiLocationsGet.useQuery({
      limit: 10000,
      orderBy: "address",
    });
  const { data: salesContracts } =
    api.endpoints.listContractsApiContractsGet.useQuery({
      limit: 10000,
      orderBy: "contract_number",
      contractType: "sales",
    });
  const { data: purchaseContracts } =
    api.endpoints.listContractsApiContractsGet.useQuery({
      limit: 10000,
      orderBy: "contract_number",
      contractType: "purchase",
    });
  const { data: cities } =
    api.endpoints.listCitiesApiLocationsCitiesGet.useQuery({
      limit: 10000,
      orderBy: "city",
    });

  const dropdownConfigs = useMemo<SalesContractsDropdowns>(() => {
    return {
      buyers:
        buyers?.items?.map((item) => ({
          label: item.name,
          value: item.id,
          data: item,
        })) ?? [],
      suppliers:
        suppliers?.items?.map((item) => ({
          label: item.name,
          value: item.id,
          data: item,
        })) ?? [],
      transporters:
        transporters?.items?.map((item) => ({
          label: item.name,
          value: item.id,
          data: item,
        })) ?? [],
      products:
        products?.items?.map((item) => ({
          label: item.name,
          value: item.id,
          data: item,
        })) ?? [],
      locations:
        locations?.items.map((location) => ({
          label: `${location.country}, ${location.city}, ${location.address}`,
          value: location.id,
          data: location,
        })) ?? [],
      drivers:
        drivers?.items.map((driver) => ({
          label: `${driver.first_name} ${driver.last_name}`,
          value: driver.id,
          data: driver,
        })) ?? [],
      salesContracts:
        salesContracts?.items.map((contract) => ({
          label: contract.contract_number,
          value: contract.id,
          data: contract,
        })) ?? [],
      purchaseContracts:
        purchaseContracts?.items.map((contract) => ({
          label: contract.contract_number,
          value: contract.id,
          data: contract,
        })) ?? [],
      cities:
        cities?.items.map((city) => ({
          label: cityCountryPairLabel(city),
          value: cityCountryPairValue(city),
          data: city,
        })) ?? [],
      boolean: booleanDropdownOptions,
    };
  }, [
    buyers,
    products,
    locations,
    salesContracts,
    purchaseContracts,
    suppliers,
    transporters,
    drivers,
    cities,
  ]);

  const handleSortChange = useCallback(
    (e: DataTableSortEvent) => onSortChange(e.sortField, e.sortOrder ?? 1),
    [onSortChange],
  );
  const handlePageChange = useCallback(
    (e: DataTablePageEvent) => onPageChange(e.page ?? 0, e.rows ?? limit),
    [onPageChange, limit],
  );

  return (
    <ViewProvider view={config.view}>
      <TableDialogs />
      <ConfirmDialog />

      <TableTitleRow<SalesContractsDropdowns, "contracts">
        visibleColumns={visibleColumns}
        columns={columns}
        setVisibleColumns={setVisibleColumns}
        globalFilterValue={globalFilterValue}
        setGlobalFilterValue={setGlobalFilterValue}
        config={config}
        dropdownConfigs={dropdownConfigs}
      />
      <DataGrid<SalesContractsDropdowns, "contracts">
        data={data}
        page={page}
        onPage={handlePageChange}
        rowsPerPage={limit}
        totalRecords={rawData?.total || 0}
        sortField={sort}
        sortOrder={order}
        onSort={handleSortChange}
        visibleColumns={visibleColumns}
        config={config}
        dropdownConfigs={dropdownConfigs}
        globalFilterValue={globalFilterValue}
        isLoading={isLoading}
      ></DataGrid>
    </ViewProvider>
  );
}
